/**
 * Phase 2 recurring baskets (Location Service Engine ``subscriptions``): a customer saves their current cart as a
 * daily or weekly basket; on its day the app offers to load it into the cart at that day's prices. No discount or
 * subscription pricing is applied — those rules are for the client to define.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Repeat, ShoppingCart, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useLse } from "../../lib/lse";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function LseBasketsPage() {
    const { has } = useLse();
    const nav = useNavigate();
    const on = has("subscriptions");
    const [rows, setRows] = useState(null);
    const [cart, setCart] = useState(null);
    const [form, setForm] = useState({ name: "Weekly groceries", frequency: "WEEKLY", weekday: new Date().getDay() === 0 ? 6 : new Date().getDay() - 1 });
    const [busy, setBusy] = useState(null);
    const load = () => api.get("/lse/baskets").then(({ data }) => setRows(data.baskets || [])).catch(() => setRows([]));
    useEffect(() => {
        if (!on) return;
        load();
        api.get("/cart").then(({ data }) => setCart(data)).catch(() => setCart(null));
    }, [on]);
    if (!on) return <div className="p-6 text-center text-sm text-muted-foreground">Not available. <Link to="/profile" className="text-primary font-semibold">Back</Link></div>;

    const save = async () => {
        if (!cart?.items?.length) return toast.error("Add items to your cart first");
        setBusy("save");
        try {
            await api.post("/lse/baskets", {
                name: form.name, restaurant_id: cart.restaurant_id, frequency: form.frequency,
                weekday: form.frequency === "WEEKLY" ? Number(form.weekday) : null,
                items: cart.items.map((i) => ({ product_id: i.product_id, variant_id: i.variant_id || null, quantity: i.quantity, name: i.name })),
            });
            toast.success("Basket saved");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); } finally { setBusy(null); }
    };
    const loadIntoCart = async (b, replace = false) => {
        setBusy(b.id);
        try {
            const { data } = await api.post(`/lse/baskets/${b.id}/load`, { replace });
            window.dispatchEvent(new Event("cart:update"));
            toast.success(`${data.added} item${data.added === 1 ? "" : "s"} in your cart${data.skipped?.length ? ` · ${data.skipped.length} unavailable` : ""}`);
            nav("/cart");
        } catch (e) {
            const d = e?.response?.data?.detail;
            if (d?.code === "CART_NOT_EMPTY") { if (window.confirm(d.message)) { setBusy(null); loadIntoCart(b, true); return; } }
            else toast.error(typeof d === "string" ? d : "Couldn't load the basket");
        } finally { setBusy(null); }
    };
    const remove = async (b) => {
        if (!window.confirm(`Delete "${b.name}"?`)) return;
        await api.delete(`/lse/baskets/${b.id}`).catch(() => {});
        load();
    };

    return (
        <div className="pb-32" data-testid="lse-baskets-page">
            <header className="px-4 pt-5 pb-4 bg-card border-b border-border flex items-center gap-3 sticky top-0 z-30">
                <Link to="/profile" className="p-1 -ml-1" aria-label="Back"><ArrowLeft size={22} /></Link>
                <h1 className="text-lg font-bold inline-flex items-center gap-2"><Repeat size={18} className="text-primary" /> My baskets</h1>
            </header>
            <section className="m-4 bg-card border border-border rounded-2xl p-4 space-y-3">
                <p className="text-sm font-semibold">Save your current cart as a basket</p>
                <p className="text-xs text-muted-foreground">{cart?.items?.length ? `${cart.items.length} item(s) from ${cart.restaurant_name}` : "Your cart is empty — add the items you buy regularly first."}</p>
                <input value={form.name} maxLength={60} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-xl bg-muted px-3 py-2.5 text-sm" aria-label="Basket name" />
                <div className="grid grid-cols-2 gap-2">
                    <select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} className="rounded-xl bg-muted px-3 py-2.5 text-sm" aria-label="How often">
                        <option value="WEEKLY">Every week</option><option value="DAILY">Every day</option>
                    </select>
                    {form.frequency === "WEEKLY" && (
                        <select value={form.weekday} onChange={(e) => setForm({ ...form, weekday: e.target.value })} className="rounded-xl bg-muted px-3 py-2.5 text-sm" aria-label="Day">
                            {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                        </select>
                    )}
                </div>
                <button type="button" onClick={save} disabled={busy === "save" || !cart?.items?.length} data-testid="lse-basket-save"
                        className="w-full min-h-[44px] rounded-xl bg-primary text-primary-foreground font-semibold text-sm disabled:opacity-50">Save basket</button>
            </section>
            <section className="mx-4 space-y-2">
                {rows === null && <p className="text-sm text-muted-foreground">Loading…</p>}
                {rows?.length === 0 && <p className="text-sm text-muted-foreground" data-testid="lse-baskets-empty">No baskets yet.</p>}
                {(rows || []).map((b) => (
                    <div key={b.id} className="bg-card border border-border rounded-2xl p-4" data-testid={`lse-basket-${b.id}`}>
                        <div className="flex items-start gap-2">
                            <div className="flex-1 min-w-0">
                                <p className="font-semibold text-sm truncate">{b.name}</p>
                                <p className="text-xs text-muted-foreground">{b.items.length} item(s) · {b.restaurant_name} · {b.frequency === "DAILY" ? "every day" : `every ${DAYS[b.weekday]}`}</p>
                            </div>
                            {b.due && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/15 text-primary">Due today</span>}
                        </div>
                        <div className="mt-3 flex gap-2">
                            <button type="button" onClick={() => loadIntoCart(b)} disabled={busy === b.id} data-testid={`lse-basket-load-${b.id}`}
                                    className="flex-1 min-h-[40px] rounded-xl bg-primary text-primary-foreground text-sm font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
                                {busy === b.id ? <Loader2 size={15} className="animate-spin" /> : <ShoppingCart size={15} />} Add to cart
                            </button>
                            <button type="button" onClick={() => remove(b)} aria-label="Delete basket" className="min-h-[40px] px-3 rounded-xl border border-border"><Trash2 size={15} /></button>
                        </div>
                    </div>
                ))}
            </section>
        </div>
    );
}
